import { QuestionnaireGroupResponse } from '@tiendanube/common';

interface Params {
  currentGroup: QuestionnaireGroupResponse;
  currentCategory: string[];
  t: (key: string) => string;
}

interface FilteredOption {
  id: number;
  code: string;
  label: string;
  description?: string;
  category?: string;
}

/**
 * Filters and transforms question options based on categories from previous answers.
 * Generic implementation that works for any question with categorized options.
 *
 * Rules:
 * - If no categories to filter by: show all options
 * - If single category: show all options from that category
 * - If multiple categories: apply category slot limits
 * - Options without category field are always shown (e.g., "skip", "other", "don't know")
 *
 * @param currentGroup - Current question configuration
 * @param currentCategory - Categories to filter by (from previous answers)
 * @param t - Translation function
 * @returns Filtered and translated options
 */
export function getFilteredAnswersTypes({
  currentGroup,
  currentCategory,
  t,
}: Params): FilteredOption[] {
  const transformOption = ({
    id,
    code,
    category,
  }: {
    id: number;
    code: string;
    category?: string;
  }): FilteredOption => ({
    id,
    code,
    label: t(`questionnaire.${currentGroup.group}.${code}`),
    description: currentGroup.hasDescriptions
      ? t(`questionnaire.description.${currentGroup.group}.${code}`)
      : undefined,
    category,
  });

  // No filtering - show all options
  if (currentCategory.length === 0) {
    return currentGroup.types.map(transformOption);
  }

  // Single category - show all from that category (existing behavior)
  if (currentCategory.length === 1) {
    return currentGroup.types
      .filter(({ category }) => !category || currentCategory.includes(category))
      .map(transformOption);
  }

  // Multiple categories - apply slot limits
  return applySlotLimits(currentGroup, currentCategory, transformOption);
}

/**
 * Applies slot limits when multiple categories are selected.
 * Groups options by category, limits each group, sorts by priority, then flattens.
 */
function applySlotLimits(
  currentGroup: {
    types: Array<{ id: number; code: string; category?: string }>;
    categoryLimits?: Record<string, { maxSlots: number; priority: number }>;
  },
  selectedCategories: string[],
  transformOption: (opt: {
    id: number;
    code: string;
    category?: string;
  }) => FilteredOption,
): FilteredOption[] {
  const categoryLimits = currentGroup.categoryLimits || {};

  // Separate uncategorized options (always shown)
  const uncategorizedOptions = currentGroup.types
    .filter(({ category }) => !category)
    .map(transformOption);

  // Group options by category
  const optionsByCategory: Record<string, FilteredOption[]> = {};

  for (const option of currentGroup.types) {
    if (!option.category) continue;

    if (selectedCategories.includes(option.category)) {
      if (!optionsByCategory[option.category]) {
        optionsByCategory[option.category] = [];
      }
      optionsByCategory[option.category].push(transformOption(option));
    }
  }

  // Apply limits and sort by priority
  const limitedOptions = Object.entries(optionsByCategory)
    .map(([category, options]) => {
      const limit = categoryLimits[category];
      const maxSlots = limit?.maxSlots ?? options.length; // No limit if not configured
      const priority = limit?.priority ?? 999; // Low priority if not configured

      return {
        category,
        priority,
        options: options.slice(0, maxSlots), // Take first N (already ordered by API)
      };
    })
    .sort((a, b) => a.priority - b.priority) // Sort by priority (lower = higher priority)
    .flatMap(({ options }) => options);

  // Return: prioritized limited options + uncategorized options at the end
  return [...limitedOptions, ...uncategorizedOptions];
}
